import type { ScenarioDelta, ScenarioType } from "../api-modules/types.js";

/**
 * Phase 6 Session 4 canonical scenario templates — mirrored from
 * `shared/src/data/scenario-templates.ts` per the 5.1 lock (web does
 * NOT import from `@hearth/shared`). Edit both files together.
 */

export interface ScenarioTemplate {
  readonly kind: ScenarioType;
  readonly default_name: string;
  readonly default_delta: ScenarioDelta;
  readonly narrative: string;
}

export const JOB_LOSS_TEMPLATE: ScenarioTemplate = {
  kind: "job_loss",
  default_name: "Job loss (18 months)",
  default_delta: {
    monthly_net_savings_override_cents: 0,
    duration_months: 18,
  },
  narrative:
    "Income drops to zero for 18 months. Tests how long the household can coast on existing savings without contributing.",
};

export const SABBATICAL_TEMPLATE: ScenarioTemplate = {
  kind: "sabbatical",
  default_name: "Sabbatical (12 months)",
  default_delta: {
    monthly_net_savings_override_cents: 0,
    duration_months: 12,
  },
  narrative:
    "Intentional 12-month pause — travel year, caregiving, reskilling. Same math as job loss; different motivation.",
};

export const SECOND_CHILD_TEMPLATE: ScenarioTemplate = {
  kind: "second_child",
  default_name: "Second child",
  default_delta: {
    annual_spend_delta_cents: 15_000_00,
    duration_months: 17 * 12,
  },
  narrative:
    "Annual spend bumps by $15k for 17 years (daycare + activities + general inflation). Excludes college (modeled separately under College Plans).",
};

export const RELOCATION_TEMPLATE: ScenarioTemplate = {
  kind: "relocation",
  default_name: "Relocate to Austin",
  default_delta: {
    relocate_to_city_code: "AUS",
  },
  narrative:
    "Move to a lower-cost-of-living city. Replay applies the NUMBEO 2026 cost-of-living multiplier to your effective annual spend.",
};

export const EMERGENCY_50K_TEMPLATE: ScenarioTemplate = {
  kind: "emergency_50k",
  default_name: "$50K emergency",
  default_delta: {
    portfolio_one_time_delta_cents: -50_000_00,
  },
  narrative:
    "One-time $50,000 hit to your portfolio (medical, home repair, family). Replay starts the simulation with the lower balance.",
};

export const RATE_CHANGE_TEMPLATE: ScenarioTemplate = {
  kind: "rate_change",
  default_name: "Lower returns (4% real)",
  default_delta: {
    expected_return_rate_pct: 4,
  },
  narrative:
    "What if real returns drop from 7% to 4% (a Bogleheads-conservative or bond-heavy world)? Tests sensitivity of FIRE date to return assumptions.",
};

export const CUSTOM_TEMPLATE: ScenarioTemplate = {
  kind: "custom",
  default_name: "Custom scenario",
  default_delta: {},
  narrative:
    "Start from scratch. Combine any overrides — withdrawal rate, return rate, spend, savings, city, portfolio jolt — to model your own what-if.",
};

export const SCENARIO_TEMPLATES: readonly ScenarioTemplate[] = [
  JOB_LOSS_TEMPLATE,
  SABBATICAL_TEMPLATE,
  SECOND_CHILD_TEMPLATE,
  RELOCATION_TEMPLATE,
  EMERGENCY_50K_TEMPLATE,
  RATE_CHANGE_TEMPLATE,
  CUSTOM_TEMPLATE,
];

export function templateForKind(kind: ScenarioType): ScenarioTemplate {
  const found = SCENARIO_TEMPLATES.find((t) => t.kind === kind);
  if (!found) {
    throw new Error(`templateForKind: unknown scenario kind: ${kind}`);
  }
  return found;
}
